import { useEffect, useState } from "react";
import { useAuth } from "./context/AuthContext";
import { pathToPage, navigate } from "./utils/navigate";
import { t } from "./i18n";
import BrandLogo from "./components/BrandLogo";
import LangBar from "./components/LangBar";
import LoginPage from "./pages/LoginPage";
import PatientHome from "./pages/PatientHome";
import MedicinePage from "./pages/MedicinePage";
import DoctorBoard from "./pages/DoctorBoard";
import WriteRx from "./pages/WriteRx";
import ClinicDoctors from "./pages/ClinicDoctors";
import WallPage from "./pages/WallPage";
import MePage from "./pages/MePage";

function App() {
  const { isAuthenticated, loading, user } = useAuth();
  const [page, setPage] = useState(() => pathToPage(window.location.pathname));
  const [lang, setLang] = useState(() => localStorage.getItem("tabletstalk_lang") || "hi");

  useEffect(() => {
    localStorage.setItem("tabletstalk_lang", lang);
  }, [lang]);

  useEffect(() => {
    if (user?.language) setLang(user.language);
  }, [user?.language]);

  useEffect(() => {
    const apply = (to) => setPage(pathToPage(to || window.location.pathname));
    const onNav = (e) => apply(e.detail?.to);
    const onPop = () => apply(window.location.pathname + window.location.search);
    window.addEventListener("app:navigate", onNav);
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("app:navigate", onNav);
      window.removeEventListener("popstate", onPop);
    };
  }, []);

  if (loading) {
    return (
      <div className="tt-boot">
        <BrandLogo height={48} />
        <p>{t(lang, "loading")}</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (page === "/wall" || page === "wall") {
      return (
        <div className="tt-app">
          <header className="tt-head">
            <BrandLogo height={32} />
            <LangBar lang={lang} onChange={setLang} />
          </header>
          <main>
            <WallPage lang={lang} guest />
          </main>
        </div>
      );
    }
    return <LoginPage lang={lang} setLang={setLang} />;
  }

  const isDoctor = !!user?.is_doctor;
  const isAdmin = !!user?.is_admin;
  const staff = isDoctor || isAdmin;
  const path = window.location.pathname;
  const rxMatch = path.match(/^\/rx\/(\d+)/);
  const staffHome = isAdmin && !isDoctor ? (
    <ClinicDoctors lang={lang} />
  ) : staff ? (
    <DoctorBoard lang={lang} />
  ) : (
    <PatientHome lang={lang} />
  );
  let body = staffHome;
  if (rxMatch) body = <MedicinePage lang={lang} rxId={rxMatch[1]} />;
  else if (page === "/write" || page === "write") {
    body = isDoctor ? <WriteRx lang={lang} /> : staffHome;
  } else if (page === "/doctors" || page === "doctors") {
    body = isAdmin ? <ClinicDoctors lang={lang} /> : staffHome;
  } else if (page === "/board" || page === "board") {
    body = staff ? <DoctorBoard lang={lang} /> : <PatientHome lang={lang} />;
  } else if (page === "/wall" || page === "wall") {
    body = <WallPage lang={lang} />;
  } else if (page === "me" || page === "/me") {
    body = <MePage lang={lang} setLang={setLang} />;
  } else if (page === "home" || page === "" || page === "/") {
    body = staffHome;
  }

  return (
    <div className="tt-app">
      <header className="tt-head">
        <BrandLogo height={32} />
        <LangBar lang={lang} onChange={setLang} />
      </header>
      <nav className="tt-nav">
        {staff ? (
          <>
            <button
              className={page === "/board" || (page === "home" && isDoctor) ? "on" : ""}
              onClick={() => navigate("/board")}
            >
              {t(lang, "board")}
            </button>
            {isAdmin && (
              <button
                className={page === "/doctors" || page === "doctors" || (page === "home" && !isDoctor) ? "on" : ""}
                onClick={() => navigate("/doctors")}
              >
                {t(lang, "doctorsNav")}
              </button>
            )}
            {isDoctor && (
              <button className={page === "/write" || page === "write" ? "on" : ""} onClick={() => navigate("/write")}>
                {t(lang, "writeRx")}
              </button>
            )}
          </>
        ) : (
          <button className={!rxMatch && page !== "/wall" && page !== "wall" ? "on" : ""} onClick={() => navigate("/")}>
            {t(lang, "myMedicines")}
          </button>
        )}
        <button className={page === "/wall" || page === "wall" ? "on" : ""} onClick={() => navigate("/wall")}>
          {t(lang, "wall")}
        </button>
        <button className={page === "me" ? "on" : ""} onClick={() => navigate("/me")}>
          {t(lang, "me")}
        </button>
      </nav>
      <main>{body}</main>
    </div>
  );
}

export default App;
